import dayjs from 'dayjs';
import React from 'react';
import 'dayjs/locale/fr';

import { Header, Icon, Label, Loader } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';
import { request } from '@mairistem/tools';
import { toast } from 'react-toastify';

import { ARRETES } from '@/constants';
import { getDocumentTemporaryUrl } from '@/routes';
import type { ArreteType } from '@/types';
import { formatMonetaire, getRegieRegisseurFullName } from '@/utils';

interface ArretePeriodiqueListeTableRowProps {
  item: ArreteType & { isPrevisionnel?: boolean };
  rowProps: Record<string, unknown>;
}

const ArretePeriodiqueListeTableRow = (props: ArretePeriodiqueListeTableRowProps) => {
  const { item, rowProps } = props;

  const {
    identifiant,
    date,
    libelle,
    dateDebutPeriode,
    dateFinPeriode,
    regisseur,
    montantTotal,
    montantTotalAnnulation,
    dateValidationOrdonnateur,
    dateValidationPosteComptable,
    documents,
    isPrevisionnel,
  } = item;

  const titrageEffectue = !!dateValidationOrdonnateur && !!dateValidationPosteComptable;

  const editionsDocument = React.useMemo(
    () => documents?.find((d) => d.categorie === ARRETES.CATEGORIE_EDITIONS_FUSIONNEES),
    [documents],
  );

  const [downloading, setDownloading] = React.useState(false);

  const handleDownloadEditions = React.useCallback(async () => {
    if (!editionsDocument) return;
    setDownloading(true);
    try {
      const { url }: { url: string } = await request.get(
        getDocumentTemporaryUrl(editionsDocument.identifiant),
      );
      window.open(url, '_blank');
    } catch {
      toast.error("Erreur lors de l'ouverture du document");
    } finally {
      setDownloading(false);
    }
  }, [editionsDocument]);

  const debutFormate = dayjs(dateDebutPeriode).format('DD/MM');
  const finFormate = dayjs(dateFinPeriode).format('DD/MM/YYYY');
  const periodeTitle = `Période du ${debutFormate} au ${finFormate}`;

  const sansEncaissement = !montantTotal || montantTotal === 0;
  const aDesAnnulations = montantTotalAnnulation && montantTotalAnnulation > 0;
  const montantTitle =
    sansEncaissement && aDesAnnulations
      ? `L'arrêté du ${dayjs(date).locale('fr').format('D MMMM YYYY')} est sans encaissement. Des annulations de reçus ont été saisies pour un montant de ${formatMonetaire(montantTotalAnnulation, 2, true)}.`
      : undefined;

  const rowKey = identifiant ?? `previsionnel-${dateFinPeriode}`;

  // ListeTable.Row n'accepte pas de prop `style` (seulement disabled/error/positive/selected).
  // On applique donc le style "prévisionnel" sur chaque Cell via `previsionnelStyle` mergé.
  const previsionnelStyle = isPrevisionnel ? { fontStyle: 'italic' as const, color: '#888' } : null;

  return (
    <ListeTable.Row key={rowKey} {...rowProps}>
      <ListeTable.Cell
        name="libelle"
        verticalAlign="middle"
        // @ts-expect-error title attribute supporté par le rendu HTML
        title={libelle}
        style={previsionnelStyle ?? undefined}
      >
        <div className="textEllipsis" style={{ paddingRight: 10 }}>
          {libelle}
        </div>
      </ListeTable.Cell>
      <ListeTable.Cell
        name="date"
        textAlign="center"
        verticalAlign="middle"
        style={{ width: 140, ...(previsionnelStyle ?? {}) }}
        // @ts-expect-error
        title={periodeTitle}
      >
        {dayjs(date).format('DD/MM/YYYY')}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="regisseur"
        verticalAlign="middle"
        style={{ width: 200, ...(previsionnelStyle ?? {}) }}
      >
        {getRegieRegisseurFullName(regisseur)}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="montantTotal"
        textAlign="right"
        verticalAlign="middle"
        style={{ width: 150, ...(previsionnelStyle ?? {}) }}
        // @ts-expect-error
        title={montantTitle}
      >
        {formatMonetaire(montantTotal ?? 0)}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="titrage"
        textAlign="center"
        style={{ width: 130 }}
        verticalAlign="middle"
      >
        {!isPrevisionnel && titrageEffectue && (
          <Label size="small" circular color="green" style={{ width: '100%' }}>
            <Header as="h5" style={{ margin: 0 }} color="green">
              Effectué
            </Header>
          </Label>
        )}
        {!isPrevisionnel && !titrageEffectue && (
          <Label size="small" circular color="orange" style={{ width: '100%' }}>
            <Header as="h5" style={{ margin: 0 }} color="orange">
              À faire
            </Header>
          </Label>
        )}
      </ListeTable.Cell>
      <ListeTable.Cell name="document" textAlign="center" style={{ width: 160 }}>
        {!isPrevisionnel && editionsDocument && downloading && <Loader size="mini" active inline />}
        {!isPrevisionnel && editionsDocument && !downloading && (
          <Icon
            name="file pdf"
            size="large"
            link
            title="Ouvrir les éditions de l'arrêté"
            fitted
            onClick={handleDownloadEditions}
          />
        )}
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default ArretePeriodiqueListeTableRow;
